import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropzone", "fileInput", "selection", "item", "selectionInput", "bulkActionBar", "bulkDeleteCount", "bulkSelectionCount", "uploadForm", "uploadOverlay", "uploadProgressBar", "uploadStatus", "uploadSubmit"]
  static values = {
    reorderUrl: String,
    uploadSessionUrl: String,
    uploadChunkUrlTemplate: String,
    uploadCompleteUrlTemplate: String
  }

  connect() {
    this.draggedItem = null
    this.uploadAbortController = null
    this.syncSelection()
    this.syncBulkDeleteState()
  }

  disconnect() {
    if (this.uploadAbortController) {
      this.uploadAbortController.abort()
      this.uploadAbortController = null
    }
  }

  openFilePicker() {
    this.fileInputTarget.click()
  }

  filesChanged() {
    this.syncSelection()
  }

  toggleSelection(event) {
    if (event.target.closest("button, a, input, label, form, [data-no-selection-toggle]")) {
      return
    }

    const input = event.currentTarget.querySelector('input[data-media-library-target="selectionInput"]')
    if (!input) return

    input.checked = !input.checked
    this.syncBulkDeleteState()
  }

  checkboxClicked(event) {
    event.stopPropagation()
  }

  selectionChanged() {
    this.syncBulkDeleteState()
  }

  clearSelection(event) {
    event?.preventDefault()

    this.selectionInputTargets.forEach((input) => {
      input.checked = false
    })

    this.syncBulkDeleteState()
  }

  stopSelectionPropagation(event) {
    event.stopPropagation()
  }

  dragOverUpload(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-zinc-950", "bg-zinc-100")
  }

  dragLeaveUpload(event) {
    if (event.currentTarget.contains(event.relatedTarget)) return

    this.resetDropzone()
  }

  dropUpload(event) {
    event.preventDefault()
    const { files } = event.dataTransfer
    if (!files || files.length === 0) return

    const transfer = new DataTransfer()
    Array.from(files).forEach((file) => transfer.items.add(file))
    this.fileInputTarget.files = transfer.files
    this.syncSelection()
    this.resetDropzone()
  }

  async submitUpload(event) {
    event.preventDefault()

    const files = Array.from(this.fileInputTarget.files || [])
    if (files.length === 0) {
      this.syncSelection()
      return
    }

    this.startUploadOverlay(files)
    this.uploadAbortController = new AbortController()

    try {
      await this.uploadFilesInChunks(files)
      this.uploadAbortController = null
      this.updateUploadOverlay(100, "Finishing upload…")
      this.finishUploadOverlay()
      this.fileInputTarget.value = ""
      this.syncSelection()
      window.location.assign(window.location.href)
    } catch (error) {
      this.uploadAbortController = null
      this.finishUploadOverlay()
      if (error.name === "AbortError") {
        return
      }
      this.updateUploadStatus(error.message)
      window.showToast?.(error.message, "error")
    }
  }

  startReorder(event) {
    this.draggedItem = event.currentTarget.closest('[data-media-library-target="item"]')
    if (!this.draggedItem) return

    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", this.draggedItem.dataset.mediaId)
    this.draggedItem.classList.add("opacity-40")
  }

  allowReorder(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
  }

  dropReorder(event) {
    event.preventDefault()
    const target = event.currentTarget

    if (!this.draggedItem || this.draggedItem === target) return

    const items = this.itemTargets
    const draggedIndex = items.indexOf(this.draggedItem)
    const targetIndex = items.indexOf(target)

    if (draggedIndex < targetIndex) {
      target.after(this.draggedItem)
    } else {
      target.before(this.draggedItem)
    }

    this.persistOrder()
  }

  endReorder(event) {
    const item = event.currentTarget.closest('[data-media-library-target="item"]')
    item?.classList.remove("opacity-40")
    this.draggedItem = null
  }

  syncSelection() {
    const files = Array.from(this.fileInputTarget.files || [])
    if (files.length === 0) {
      this.selectionTarget.textContent = "Drop media here or choose files"
      return
    }

    const imageCount = files.filter((file) => this.imageFile(file)).length
    const videoCount = files.filter((file) => this.videoFile(file)).length
    const parts = []

    if (imageCount > 0) {
      parts.push(`${imageCount} ${imageCount === 1 ? "photo" : "photos"}`)
    }

    if (videoCount > 0) {
      parts.push(`${videoCount} ${videoCount === 1 ? "video" : "videos"}`)
    }

    this.selectionTarget.textContent = parts.join(" and ")
  }

  syncBulkDeleteState() {
    if (!this.hasSelectionInputTarget) {
      return
    }

    const selectedCount = this.selectionInputTargets.filter((input) => input.checked).length

    this.selectionInputTargets.forEach((input) => {
      const item = input.closest('[data-media-library-target="item"]')
      if (!item) return

      if (item.dataset) {
        item.dataset.selected = input.checked ? "true" : "false"
      } else if (typeof item.setAttribute === "function") {
        item.setAttribute("data-selected", input.checked ? "true" : "false")
      }
    })

    if (this.hasBulkDeleteCountTarget) {
      this.bulkDeleteCountTarget.textContent = String(selectedCount)
    }

    if (this.hasBulkSelectionCountTarget) {
      this.bulkSelectionCountTarget.textContent = String(selectedCount)
    }

    if (this.hasBulkActionBarTarget) {
      this.bulkActionBarTarget.dataset.state = selectedCount > 0 ? "visible" : "hidden"
    }
  }

  startUploadOverlay(files) {
    this.updateUploadOverlay(0, this.uploadSummary(files))
    this.uploadOverlayTarget.classList.remove("hidden")
    this.uploadSubmitTarget.disabled = true
    this.uploadSubmitTarget.classList.add("opacity-60")
  }

  updateUploadOverlay(percent, statusText = null) {
    this.uploadProgressBarTarget.style.width = `${percent}%`

    if (statusText) {
      this.updateUploadStatus(statusText)
    }
  }

  finishUploadOverlay() {
    this.uploadOverlayTarget.classList.add("hidden")
    this.uploadProgressBarTarget.style.width = "0%"
    this.uploadSubmitTarget.disabled = false
    this.uploadSubmitTarget.classList.remove("opacity-60")
  }

  updateUploadStatus(text) {
    this.uploadStatusTarget.textContent = text
  }

  resetDropzone() {
    this.dropzoneTarget.classList.remove("border-zinc-950", "bg-zinc-100")
  }

  persistOrder() {
    const mediaIds = this.itemTargets.map((item) => item.dataset.mediaId)

    fetch(this.reorderUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
        Accept: "text/vnd.turbo-stream.html, text/html, application/xhtml+xml"
      },
      body: JSON.stringify({ media_public_ids: mediaIds })
    })
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  async uploadFilesInChunks(files) {
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0)
    let uploadedBytes = 0

    for (const [index, file] of files.entries()) {
      this.updateUploadStatus(`Uploading ${file.name} (${index + 1} of ${files.length})…`)
      const session = await this.createUploadSession(file)
      const chunkSize = Number(session.chunk_size)
      const totalChunks = Math.ceil(file.size / chunkSize)

      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
        const chunk = file.slice(chunkIndex * chunkSize, Math.min(file.size, (chunkIndex + 1) * chunkSize))
        await this.uploadChunk(session.upload_id, chunkIndex, chunk)
        uploadedBytes += chunk.size
        this.updateUploadOverlay(this.uploadPercent(uploadedBytes, totalBytes))
      }

      await this.completeUploadSession(session.upload_id)
    }
  }

  async createUploadSession(file) {
    const response = await fetch(this.uploadSessionUrlValue, {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        filesize: file.size
      }),
      signal: this.uploadAbortController.signal
    })

    return this.parseJsonResponse(response)
  }

  async uploadChunk(uploadId, chunkIndex, chunk) {
    const response = await fetch(this.expandUploadChunkUrl(uploadId, chunkIndex), {
      method: "PUT",
      headers: {
        "Content-Type": "application/octet-stream",
        "X-CSRF-Token": this.csrfToken,
        Accept: "application/json"
      },
      body: chunk,
      signal: this.uploadAbortController.signal
    })

    if (!response.ok) {
      throw new Error(await this.errorMessageFor(response))
    }
  }

  async completeUploadSession(uploadId) {
    const response = await fetch(this.expandUploadCompleteUrl(uploadId), {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ preserve_order: false }),
      signal: this.uploadAbortController.signal
    })

    await this.parseJsonResponse(response)
  }

  async parseJsonResponse(response) {
    let payload = {}

    try {
      payload = await response.json()
    } catch {
      payload = {}
    }

    if (!response.ok) {
      throw new Error(this.errorMessageFromPayload(payload))
    }

    return payload
  }

  async errorMessageFor(response) {
    try {
      const payload = await response.json()
      return this.errorMessageFromPayload(payload)
    } catch {
      return "Upload failed. Please try again."
    }
  }

  errorMessageFromPayload(payload) {
    const firstError = payload?.errors?.[0]
    return firstError || payload?.error || "Upload failed. Please try again."
  }

  jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": this.csrfToken,
      Accept: "application/json"
    }
  }

  expandUploadChunkUrl(uploadId, chunkIndex) {
    return this.uploadChunkUrlTemplateValue
      .replace("__UPLOAD_ID__", encodeURIComponent(uploadId))
      .replace("__CHUNK_INDEX__", String(chunkIndex))
  }

  expandUploadCompleteUrl(uploadId) {
    return this.uploadCompleteUrlTemplateValue
      .replace("__UPLOAD_ID__", encodeURIComponent(uploadId))
  }
  uploadPercent(uploadedBytes, totalBytes) {
    if (totalBytes <= 0) {
      return 100
    }

    return Math.max(5, Math.min(100, Math.round((uploadedBytes / totalBytes) * 100)))
  }

  imageFile(file) {
    return file.type.startsWith("image/")
  }

  videoFile(file) {
    return file.type === "video/webm" || file.type === "video/mp4"
  }

  uploadSummary(files) {
    const imageCount = files.filter((file) => this.imageFile(file)).length
    const videoCount = files.filter((file) => this.videoFile(file)).length
    const parts = []

    if (imageCount > 0) {
      parts.push(`${imageCount} ${imageCount === 1 ? "photo" : "photos"}`)
    }

    if (videoCount > 0) {
      parts.push(`${videoCount} ${videoCount === 1 ? "video" : "videos"}`)
    }

    return parts.length > 0 ? `Uploading ${parts.join(" and ")}…` : "Uploading media…"
  }

  replaceDocument(html) {
    if (!html) {
      return
    }

    document.open()
    document.write(html)
    document.close()
  }
}
